import Box from "@mui/material/Box";
import React, { Profiler, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";

import { Timings } from "@storyboard/web-logger/timings/Timings";
import FleetMessagingModal from "@/components/FleetMessagingModal";
import { client } from "@/config";
import { buildDriverActivity, buildDriverThread } from "@/features/western-express/boards";
import { DriverMessageView } from "@/features/western-express/DriverMessageView";
import { buildDemoDrivers, type RealDriverInput } from "@/features/western-express/demo/overlay";
import { FreightStatusBoard } from "@/features/western-express/FreightStatusView";
import { useDriverFilters, useDriverQuery, useMyDriverUnreadCount } from "@/hooks/driver";
import { buildDriverViewSearchTargets } from "@/hooks/driver/driverSearchTargets";
import { useAvailableGroups } from "@/hooks/driver/useAvailableGroups";
import { useDriverGridState } from "@/hooks/driver/useDriverGridState";
import { useDriverTags } from "@/hooks/driver/useDriverTags";
import { useUnactivatedMembers } from "@/hooks/driver/useUnactivatedMembers";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";
import { groupsQuery } from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { UserContext } from "@/models/StateProviders/userProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { UxContext } from "@/models/UxStateProvider";
import { findDriverUsernameTag } from "./columns";
import { type TagFilterState, useColumnFilters } from "./DriverColumnFilters";
import DriverDataGrid from "./DriverDataGrid";
import DriverMetaRow from "./DriverMetaRow";
import DriverToolbar, { DriverTabs, TeamDropdown } from "./DriverToolbar";
import UnreadMessagesFilterButton from "./UnreadMessagesFilterButton";
import { zinc } from "./ui/colors";
import { FeedPanelContext } from "./ui/FeedPanelContext";

const TABLE_PANEL_ID = "driver-table";
const FEED_PANEL_ID = "driver-feed";

/** Stable ref for `useUnactivatedMembers` — inline `["limitedMember"]` would change every render. */
const UNACTIVATED_DRIVER_ROLES: string[] = ["limitedMember"];

function onRenderCallback(id: string, phase: string, actualDuration: number) {
  if (actualDuration > 5) {
    Timings.createSpan(`react-render:${id}`, performance.now() - actualDuration, performance.now(), {
      properties: { phase, duration: actualDuration },
    });
  }
}

function useTagFilterState(): TagFilterState {
  const [tagFilters, setTagFilters] = useState<Record<string, string[]>>({});
  const [tagSort, setTagSort] = useState<{ tagId: string; direction: "asc" | "desc" } | null>(null);

  const hasActiveTagFilters = useMemo(() => Object.values(tagFilters).some((v) => v.length > 0), [tagFilters]);

  const toggleTagFilterValue = useCallback((tagId: string, value: string) => {
    setTagFilters((prev) => {
      const current = prev[tagId] ?? [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [tagId]: next };
    });
  }, []);

  const selectTagFilterValues = useCallback((tagId: string, values: string[]) => {
    setTagFilters((prev) => {
      const current = new Set(prev[tagId] ?? []);
      for (const v of values) current.add(v);
      return { ...prev, [tagId]: [...current] };
    });
  }, []);

  const clearTagFilter = useCallback((tagId: string) => {
    setTagFilters((prev) => {
      const next = { ...prev };
      delete next[tagId];
      return next;
    });
  }, []);

  const clearAllTagFilters = useCallback(() => {
    setTagFilters({});
  }, []);

  return {
    tagFilters,
    toggleTagFilterValue,
    selectTagFilterValues,
    clearTagFilter,
    clearAllTagFilters,
    hasActiveTagFilters,
    tagSort,
    setTagSort,
  };
}

export default React.memo(function DriverView() {
  const { myAccount } = useContext(MyAccountContext);
  const { setDispatcherFunction } = useContext(UserContext);
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const navigate = useNavigate();
  const { setFleetMessageModalOpen, setFleetMessageActiveChannels, setFleetMessageFromDriverTable } =
    useContext(UxContext);
  const { workspaceMembershipId: activeMembershipId } = useParams();

  const { unactivatedMembershipIds, unactivatedMembers } = useUnactivatedMembers(UNACTIVATED_DRIVER_ROLES);
  const unactivatedIds = useMemo(() => [...unactivatedMembershipIds], [unactivatedMembershipIds]);

  const { tags, tagValueMap } = useDriverTags();
  const driverViewSearchTargets = useMemo(() => buildDriverViewSearchTargets(tags), [tags]);

  const filters = useDriverFilters(
    workspaceId ?? "",
    myCurrentWorkspaceMembership?.id ?? "",
    myAccount?.preferredLanguage ?? "en",
    unactivatedIds,
    driverViewSearchTargets,
  );
  const tagFilterState = useTagFilterState();

  const { drivers, allAliases, totalDriverCount, visibleMembershipIds } = useDriverQuery(
    workspaceId ?? "",
    myCurrentWorkspaceMembership?.id,
    filters.config,
    tagValueMap,
    tagFilterState.tagFilters,
    tagFilterState.tagSort,
    unactivatedMembers,
    tags,
  );

  const myDriverUnreadCount = useMyDriverUnreadCount(
    workspaceId ?? "",
    myCurrentWorkspaceMembership?.id,
    filters.config.teamAliases,
  );

  const leadingTagColumnId = useMemo(() => {
    const tag = findDriverUsernameTag(tags);
    return tag ? `tag:${tag.id}` : null;
  }, [tags]);
  const gridState = useDriverGridState(workspaceId ?? "", leadingTagColumnId);

  const groupQueryResults =
    useReactiveSelectQuery(
      groupsQuery,
      [{ workspaceId, myCurrentWorkspaceMembershipId: myCurrentWorkspaceMembership?.id }],
      "DriverView:groupsQuery",
    ) ?? [];

  const availableGroups = useAvailableGroups(groupQueryResults, filters.groupIds);
  const { getColumnExtra, isFilterActive, getActiveFilterValues, showUnreadOnly, toggleUnreadOnly } = useColumnFilters({
    filters,
    groups: availableGroups,
    tagValueMap,
    visibleMembershipIds,
    tagFilterState,
    tags,
  });

  const filteredDrivers = useMemo(
    () => (showUnreadOnly ? drivers.filter((d) => d.unread) : drivers),
    [drivers, showUnreadOnly],
  );

  // Prototype seeding: overlay the same demo data the Tasks/Freight/active-context
  // views use so the fleet list reads as one consistent fake fleet — seeded name,
  // location, and most-recent message — while keeping the grid UI unchanged.
  const seededDrivers = useMemo(
    () =>
      filteredDrivers.map((row) => {
        const demo = buildDemoDrivers([
          {
            workspaceMembershipId: row.workspaceMembershipId,
            name: row.name,
            alias: row.alias,
            feedId: row.feedId,
            accountId: row.accountId,
            location: row.location,
          },
        ])[0];
        const thread = buildDriverThread(row.workspaceMembershipId);
        return {
          ...row,
          name: demo.name,
          location: demo.location,
          recentMessage: thread[thread.length - 1]?.preview ?? row.recentMessage,
        };
      }),
    [filteredDrivers],
  );

  /** Freight Status board (prototype): real roster overlaid with demo freight context. */
  const isFreightTab = filters.effectiveTab === "freight";
  const freightDrivers = useMemo(
    () => (isFreightTab ? buildDemoDrivers(filteredDrivers) : []),
    [isFreightTab, filteredDrivers],
  );

  const isNeverLoggedInOnly =
    filters.config.statusFilter.length === 1 && filters.config.statusFilter[0] === "never-logged-in";
  /** `driverTotalCount` omits the client-merged unactivated list; for this filter, total must match the roster. */
  const driverMetaTotalCount = isNeverLoggedInOnly ? drivers.length : totalDriverCount;

  const selectedCount = useMemo(
    () => filteredDrivers.filter((d) => gridState.rowSelection[d.workspaceMembershipId]).length,
    [filteredDrivers, gridState.rowSelection],
  );

  const selectedFeedIds = useMemo(
    () =>
      filteredDrivers
        .filter((d) => gridState.rowSelection[d.workspaceMembershipId])
        .map((d) => d.feedId)
        .filter((id): id is string => id != null),
    [filteredDrivers, gridState.rowSelection],
  );

  const handleFleetMessage = useCallback(() => {
    setFleetMessageFromDriverTable(() => selectedFeedIds?.length > 0);
    setFleetMessageActiveChannels(selectedFeedIds);
    setFleetMessageModalOpen(true);
  }, [selectedFeedIds, setFleetMessageActiveChannels, setFleetMessageModalOpen, setFleetMessageFromDriverTable]);

  const handleFleetMessageClose = useCallback(() => {
    gridState.onRowSelectionChange({});
  }, [gridState.onRowSelectionChange]);

  const handleTeamChange = useCallback(
    async (team: string) => {
      setDispatcherFunction?.(team);
      if (workspaceId && myCurrentWorkspaceMembership?.id) {
        try {
          await client.workspacesClient.membershipsConfigPartialUpdate(workspaceId, myCurrentWorkspaceMembership.id, {
            dispatcherFunction: team,
          });
        } catch {
          /* swallow — optimistic UI already applied */
        }
      }
    },
    [workspaceId, myCurrentWorkspaceMembership?.id, setDispatcherFunction],
  );

  const { feedId } = useParams();
  const feedPanelRef = usePanelRef();
  const [feedPanelSizes, setFeedPanelSizes] = useSafeLocalStorage("driver-feed-panels", {
    table: 65,
    feed: 35,
  });
  const [feedCollapsed, setFeedCollapsed] = useState(!feedId);

  useEffect(() => {
    if (feedId) {
      const panel = feedPanelRef.current;
      if (panel?.isCollapsed()) panel.expand();
    }
  }, [feedId]);

  const closeFeedPanel = useCallback(() => {
    const panel = feedPanelRef.current;
    if (!panel || panel.isCollapsed()) return;
    panel.collapse();
  }, []);

  const expandFeedPanel = useCallback(() => {
    const panel = feedPanelRef.current;
    if (panel?.isCollapsed()) panel.expand();
  }, []);

  const handleFeedPanelLayout = useCallback(
    (data: Record<string, number>) => {
      const table = data[TABLE_PANEL_ID];
      const feed = data[FEED_PANEL_ID];
      if (table == null || feed == null) return;
      if (feed > 0) setFeedPanelSizes({ table, feed });
    },
    [setFeedPanelSizes],
  );

  // Open channel → the unified, seeded message-history pane (prototype). Identity
  // and thread/activity are seeded the same way as the rest of the prototype.
  const channelDriver = useMemo(() => {
    if (!feedId || !activeMembershipId) return null;
    const row = drivers.find((d) => d.workspaceMembershipId === activeMembershipId);
    const input: RealDriverInput = {
      workspaceMembershipId: activeMembershipId,
      name: row?.name ?? null,
      alias: row?.alias ?? null,
      feedId: row?.feedId ?? feedId,
      accountId: row?.accountId ?? null,
      location: row?.location ?? null,
    };
    return buildDemoDrivers([input])[0];
  }, [feedId, activeMembershipId, drivers]);
  const channelMessages = useMemo(
    () => (activeMembershipId ? buildDriverThread(activeMembershipId) : []),
    [activeMembershipId],
  );
  const channelActivity = useMemo(
    () => (activeMembershipId ? buildDriverActivity(activeMembershipId) : []),
    [activeMembershipId],
  );

  const dataGrid = (
    <DriverDataGrid
      drivers={seededDrivers}
      activeMembershipId={activeMembershipId ?? null}
      workspaceId={workspaceId ?? ""}
      myWorkspaceMembershipId={myCurrentWorkspaceMembership?.id ?? ""}
      columnVisibility={gridState.columnVisibility}
      onColumnVisibilityChange={gridState.onColumnVisibilityChange}
      columnOrder={gridState.columnOrder}
      onColumnOrderChange={gridState.onColumnOrderChange}
      columnSizing={gridState.columnSizing}
      onColumnSizingChange={gridState.onColumnSizingChange}
      rowSelection={gridState.rowSelection}
      onRowSelectionChange={gridState.onRowSelectionChange}
      getColumnExtra={getColumnExtra}
      isFilterActive={isFilterActive}
      getActiveFilterValues={getActiveFilterValues}
      showUnreadOnly={showUnreadOnly}
      toggleUnreadOnly={toggleUnreadOnly}
      tags={tags}
      onRowClick={expandFeedPanel}
    />
  );

  const tabs = <DriverTabs filters={filters} myDriverUnreadCount={myDriverUnreadCount} />;

  const toolbar = (
    <DriverToolbar
      filters={filters}
      selectedCount={selectedCount}
      onFleetMessage={handleFleetMessage}
      columnVisibility={gridState.columnVisibility}
      onColumnVisibilityChange={gridState.onColumnVisibilityChange}
      tags={tags}
      tagFilterState={tagFilterState}
    />
  );

  const unreadFilterButton = <UnreadMessagesFilterButton active={showUnreadOnly} onToggle={toggleUnreadOnly} />;

  const metaRow = (
    <DriverMetaRow
      totalCount={driverMetaTotalCount}
      selectedCount={selectedCount}
      visibleCount={filteredDrivers.length}
    />
  );

  return (
    <Profiler id="DriverView" onRender={onRenderCallback}>
      <Box sx={{ bgcolor: "black", height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Group
            defaultLayout={{
              [TABLE_PANEL_ID]: feedId ? Math.min(feedPanelSizes.table, 65) : 100,
              [FEED_PANEL_ID]: feedId ? Math.max(feedPanelSizes.feed, 35) : 0,
            }}
            onLayoutChanged={handleFeedPanelLayout}
          >
            <Panel id={TABLE_PANEL_ID}>
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", pt: 2.5, bgcolor: "black" }}>
                  {tabs}
                  <Box sx={{ flexGrow: 1 }} />
                  {!isFreightTab && <TeamDropdown filters={filters} onTeamChange={handleTeamChange} />}
                </Box>
                {isFreightTab ? (
                  <Box sx={{ flex: 1, bgcolor: zinc[800], position: "relative", overflow: "hidden" }}>
                    <Box sx={{ position: "absolute", inset: 0 }}>
                      <FreightStatusBoard
                        drivers={freightDrivers}
                        onOpenDriver={(driver) => {
                          if (!workspaceId || !driver.feedId) return;
                          navigate(`/workspaces/${workspaceId}/drivers/${driver.id}/channel/${driver.feedId}`);
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <>
                    {toolbar}
                    {unreadFilterButton}
                    {metaRow}
                    <Box sx={{ flex: 1, bgcolor: zinc[800], position: "relative", overflow: "hidden" }}>{dataGrid}</Box>
                  </>
                )}
              </Box>
            </Panel>
            <Separator className={`separator ${feedCollapsed ? "hidden" : ""}`} />
            <Panel
              id={FEED_PANEL_ID}
              minSize={500}
              maxSize="80"
              collapsible
              collapsedSize={0}
              panelRef={feedPanelRef}
              onResize={(size) => setFeedCollapsed(size.asPercentage === 0)}
            >
              {feedId && activeMembershipId && channelDriver && (
                <FeedPanelContext.Provider value={{ closePanel: closeFeedPanel }}>
                  <DriverMessageView
                    key={activeMembershipId}
                    driverName={channelDriver.name}
                    truck={channelDriver.truck}
                    messages={channelMessages}
                    activity={channelActivity}
                    onBack={closeFeedPanel}
                  />
                </FeedPanelContext.Provider>
              )}
            </Panel>
          </Group>
        </Box>
      </Box>
      <FleetMessagingModal closeModal={handleFleetMessageClose} />
    </Profiler>
  );
});
